import { PLAN_CYCLE, type Plan } from "@/services/plans/types";
import { isFreePlan } from "./is-free-plan";

export type CycleView = {
  /** Ciclo efetivamente exibido. `undefined` só quando não há plano nenhum. */
  activeCycle: PLAN_CYCLE | undefined;
  /** Alternar mensal/anual só faz sentido se houver oferta paga nos dois. */
  showCycleSwitch: boolean;
  /** Planos da vitrine no ciclo ativo, com o gratuito sempre presente. */
  plans: Plan[];
};

/**
 * O plano gratuito não tem ciclo de cobrança — não há o que cobrar.
 *
 * Enquanto a vitrine filtrava por `plan.cycle === activeCycle` sem exceção, a
 * única forma de manter o Essencial visível no anual era cadastrar um segundo
 * grátis. Isso quebrava do lado da API: `resolveFreeEntitlements` escolhe o mais
 * antigo entre os planos de preço zero e ignora o outro, então editar limites no
 * cadastro anual não mudava nada para quem não assina — a vitrine anunciava um
 * teto e o produto aplicava outro.
 *
 * Aqui o gratuito atravessa os dois ciclos com um cadastro só. O ciclo dele
 * também não conta como oferta disponível: com um único plano pago, o switch
 * levaria a duas telas idênticas.
 */
export function resolveCycleView(
  plans: Plan[],
  selectedCycle: PLAN_CYCLE,
): CycleView {
  const pagos = plans.filter((plan) => !isFreePlan(plan));
  const ciclosPagos = new Set(pagos.map((plan) => plan.cycle));

  const activeCycle = ciclosPagos.has(selectedCycle)
    ? selectedCycle
    : (pagos[0]?.cycle ?? plans[0]?.cycle ?? undefined);

  const vigenteId = resolveGratuitoVigente(plans)?.id;

  return {
    activeCycle,
    showCycleSwitch: ciclosPagos.size > 1,
    plans: activeCycle
      ? plans.filter((plan) =>
          isFreePlan(plan) ? plan.id === vigenteId : plan.cycle === activeCycle,
        )
      : [],
  };
}

/**
 * Base com mais de um plano de preço zero é erro de cadastro, e a API já avisa
 * disso no log. Enquanto o duplicado existir, a vitrine escolhe pelo mesmo
 * critério que `resolveFreeEntitlements` usa do lado de lá — o `createdAt` mais
 * antigo. Escolher diferente seria pior que mostrar os dois cards: a página
 * anunciaria os limites de um cadastro e o produto aplicaria os do outro.
 */
function resolveGratuitoVigente(plans: Plan[]): Plan | undefined {
  const gratuitos = plans.filter(isFreePlan);
  if (gratuitos.length < 2) {
    return gratuitos[0];
  }

  return [...gratuitos].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
  )[0];
}
